import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  canonicalizeDropInPaymentOptions,
  formatDate,
  formatMoney,
} from "@bw/shared/utils";
import type { DropInPaymentOption } from "@bw/shared/types";

export type PaymentMethod = "single" | "package" | "membership";

export type PaymentSelection =
  | { method: "single" }
  | { method: "package"; subscriptionId: string }
  | { method: "membership"; subscriptionId: string };

interface PaymentMethodSelectProps {
  options: DropInPaymentOption;
  value: PaymentSelection | null;
  onChange: (value: PaymentSelection) => void;
  disabled?: boolean;
}

export function admissionUnavailableMessage(
  options: Pick<
    DropInPaymentOption,
    "admission_unavailable_reason" | "minimum_remaining_minutes"
  >,
): string {
  const minimumRemaining = options.minimum_remaining_minutes;
  if (
    options.admission_unavailable_reason === "closing_soon" &&
    typeof minimumRemaining === "number" &&
    Number.isInteger(minimumRemaining) &&
    minimumRemaining > 0
  ) {
    return `New check-ins are closed because fewer than ${minimumRemaining} minutes remain.`;
  }
  return "This facility isn't running drop-in right now.";
}

/**
 * A Material radio group backed exclusively by server-returned admission
 * options. A sole paid-card option is fixed information, not a false choice.
 */
export function PaymentMethodSelect({
  options,
  value,
  onChange,
  disabled,
}: PaymentMethodSelectProps) {
  const canonicalOptions = canonicalizeDropInPaymentOptions(options);
  const hasSingle =
    canonicalOptions.single_price !== null &&
    canonicalOptions.single_price !== undefined &&
    canonicalOptions.single_visit_available;
  const cardPaymentsUnavailable =
    canonicalOptions.single_unavailable_reason === "card_payments_not_ready";
  const singleIsFree =
    hasSingle && canonicalOptions.single_requires_payment_method === false;
  const memberships = canonicalOptions.active_memberships ?? [];
  const packages = canonicalOptions.active_packages ?? [];
  const singleTotal =
    canonicalOptions.single_total ?? canonicalOptions.single_price;
  const singleDetail = canonicalOptions.campaign
    ? `${canonicalOptions.campaign.name} · save ${formatMoney(
        canonicalOptions.campaign.discount,
        canonicalOptions.currency,
      )}`
    : "Pay by card";
  const isOnlyPaidCardOption =
    hasSingle &&
    canonicalOptions.single_requires_payment_method === true &&
    memberships.length === 0 &&
    packages.length === 0;

  if (!canonicalOptions.active_now) {
    return (
      <Paper variant="outlined" role="status" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {admissionUnavailableMessage(canonicalOptions)}
        </Typography>
      </Paper>
    );
  }

  if (isOnlyPaidCardOption) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <PaymentSummary
          label="Pay drop-in fee"
          detail={singleDetail}
          trailing={formatMoney(singleTotal, canonicalOptions.currency)}
        />
      </Paper>
    );
  }

  function selectionValue(selection: PaymentSelection): string {
    return selection.method === "single"
      ? "single"
      : `${selection.method}:${selection.subscriptionId}`;
  }

  const selectedValue = value ? selectionValue(value) : "";

  return (
    <FormControl
      component="fieldset"
      fullWidth
      disabled={disabled}
      sx={{ minWidth: 0 }}
    >
      <FormLabel
        component="legend"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          p: 0,
          m: "-1px",
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        Choose your payment method
      </FormLabel>
      <RadioGroup
        aria-label="Choose how you're paying for this drop-in"
        name="paymethod"
        value={selectedValue}
      >
        <Stack spacing={1}>
          {memberships.map((membership) => {
            const selection: PaymentSelection = {
              method: "membership",
              subscriptionId: membership.subscription_id,
            };
            return (
              <PaymentRow
                key={`membership-${membership.subscription_id}`}
                value={selectionValue(selection)}
                label="Membership"
                detail={membership.option_label}
                trailing="No charge"
                onSelect={() => onChange(selection)}
              />
            );
          })}

          {packages.map((visitPackage) => {
            const selection: PaymentSelection = {
              method: "package",
              subscriptionId: visitPackage.subscription_id,
            };
            const expiry = visitPackage.expires_at
              ? `Expires ${formatDate(visitPackage.expires_at)}`
              : visitPackage.expires_at === null
                ? "No expiration"
                : "Expiration unavailable";
            return (
              <PaymentRow
                key={`package-${visitPackage.subscription_id}`}
                value={selectionValue(selection)}
                label={`${visitPackage.option_label} · spend 1`}
                detail={`${visitPackage.remaining_visits} of ${visitPackage.total_visits} left · ${expiry}`}
                trailing="−1 pass"
                onSelect={() => onChange(selection)}
              />
            );
          })}

          {hasSingle ? (
            <PaymentRow
              value="single"
              label={singleIsFree ? "Free drop-in" : "Pay drop-in fee"}
              detail={
                singleIsFree ? "No payment method required" : singleDetail
              }
              trailing={formatMoney(singleTotal, canonicalOptions.currency)}
              onSelect={() => onChange({ method: "single" })}
            />
          ) : null}

          {cardPaymentsUnavailable ? (
            <Paper variant="outlined" role="status" sx={{ p: 2 }}>
              <PaymentSummary
                label="Pay drop-in fee"
                detail="Card payments are not available at this facility yet."
              />
            </Paper>
          ) : null}
        </Stack>
      </RadioGroup>
    </FormControl>
  );
}

function PaymentRow({
  value,
  label,
  detail,
  trailing,
  onSelect,
}: {
  value: string;
  label: string;
  detail: string;
  trailing: string;
  onSelect: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <FormControlLabel
        value={value}
        onChange={onSelect}
        control={<Radio />}
        sx={{
          width: "100%",
          minHeight: 64,
          m: 0,
          px: 1.25,
          py: 0.5,
          alignItems: "center",
          "& .MuiFormControlLabel-label": { minWidth: 0, flex: 1 },
        }}
        label={
          <PaymentSummary label={label} detail={detail} trailing={trailing} />
        }
      />
    </Paper>
  );
}

function PaymentSummary({
  label,
  detail,
  trailing,
}: {
  label: string;
  detail: string;
  trailing?: string;
}) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </Box>
      {trailing ? (
        <Typography sx={{ flexShrink: 0, fontWeight: 700 }}>
          {trailing}
        </Typography>
      ) : null}
    </Stack>
  );
}
